import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Search, 
  Filter, 
  Calendar, 
  Clock, 
  FileText, 
  AlertCircle,
  ArrowUpRight,
  Sparkles,
  Download,
  Share2,
  LayoutList,
  LayoutGrid,
  Loader2,
  BookOpen,
  PlusCircle
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  Dialog, 
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "../components/ui/dropdown-menu";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../lib/utils";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../lib/api";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";

export default function Provincial() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState("active");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedSourceName, setSelectedSourceName] = useState<string>("all");
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const contentTopRef = useRef<HTMLDivElement>(null);

  // Check if company has any municipalities monitoring
  const { data: monitoredMunicipalities } = useQuery({
    queryKey: ['company-municipalities-all'],
    queryFn: async () => {
      try {
        const data = await apiClient.companyMunicipalities.listAll();
        return data;
      } catch (error) {
        console.error('[Provincial] Error fetching municipalities:', error);
        return [];
      }
    },
  });

  // Fetch provincial public events from public_events table
  const { data: provincialData, isLoading } = useQuery({
    queryKey: ['provincial-public-events'],
    queryFn: async () => {
      try {
        const data = await apiClient.provincial.publicEvents(200, 0);
        console.log('[Provincial] Fetched data:', data?.length, data);
        return data;
      } catch (error) {
        console.error('[Provincial] Error fetching data:', error);
        return [];
      }
    },
  });

  // Helper function to format source abbreviation (Ministry of Finance -> MoF, Ministry of X -> MoX)
  const formatSourceShort = (sourceName: string) => {
    if (!sourceName) return "PROV";
    // Check if it starts with "Ministry of"
    if (sourceName.toLowerCase().startsWith("ministry of")) {
      const parts = sourceName.split(" ");
      if (parts.length >= 3) {
        // Get first letter of "Ministry", "of", and the ministry name
        return `Mo${parts[2][0].toUpperCase()}`;
      }
    }
    // Otherwise use first 3 letters
    return sourceName.substring(0, 3).toUpperCase();
  };

  // Helper function to format date to YYYY-MM-DD
  const formatDateOnly = (dateStr: string | null | undefined) => {
    if (!dateStr) return "N/A";
    try {
      const date = parseISO(dateStr);
      return format(date, 'yyyy-MM-dd');
    } catch (e) {
      return dateStr.split('T')[0]; // Fallback to just taking the date part
    }
  };

  // Helper function to format date to "Mar 25 2025" format (no time)
  const formatDateShort = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    try {
      const date = parseISO(dateStr);
      return format(date, 'MMM d yyyy');
    } catch (e) {
      return null;
    }
  };

  // Helper function to check if item is ERO or ORR
  const isEROorORR = (sourceName: string | null | undefined, site: string | null | undefined) => {
    // Check site field first (most reliable) - this is the short code like "ERO" or "ORR"
    if (site) {
      const siteUpper = site.toUpperCase().trim();
      if (siteUpper === 'ERO' || siteUpper === 'ORR') {
        return true;
      }
    }
    // Fallback to source_name - check for exact matches or partial matches
    if (sourceName) {
      const name = sourceName.toUpperCase().trim();
      if (name === 'ERO' || name === 'ORR') {
        return true;
      }
      // Check for full names
      if (name.includes('ENVIRONMENTAL REGISTRY') || 
          name.includes('ONTARIO REGULATORY REGISTRY') ||
          name.includes('ENVIRONMENTAL REGISTRY OF ONTARIO')) {
        return true;
      }
    }
    return false;
  };

  // Transform provincial data to expected format
  const trackingItems = provincialData ? provincialData.map((item: any) => {
    const isEROORR = isEROorORR(item.source_name, item.site);
    const commentStartDate = isEROORR ? formatDateShort(item.event_start) : null;
    const commentEndDate = isEROORR ? formatDateShort(item.comments_due_date) : null;
    
    return {
      id: item.id,
      title: item.title,
      source: item.source || item.source_name || "Ontario Registry",
      sourceName: item.source_name || item.source || "Ontario Registry",
      sourceShort: formatSourceShort(item.source_name || item.source),
      status: item.status || "Open",
      postedDate: formatDateOnly(item.posted_date || item.event_start),
      deadline: formatDateOnly(item.deadline_date || item.event_end) || "N/A",
      description: item.description || "",
      impact: item.impact || "Medium",
      category: item.category || item.committee_name || "General",
      url: item.url || item.external_reference_id || null,
      site: item.site || "",
      // ERO/ORR specific fields
      isEROORR: isEROORR,
      bill_act: item.bill_act || "",
      instrument_type: item.instrument_type || item.event_type || "",
      category_name: item.category_name || "",
      ministry_name: item.ministry_name || "",
      comments_due_date: item.comments_due_date || null,
      commentPeriod: isEROORR && (commentStartDate || commentEndDate)
        ? commentStartDate && commentEndDate
          ? `${commentStartDate} - ${commentEndDate}`
          : commentStartDate || commentEndDate || null
        : null,
      event_type: item.event_type || "", // Keep for non-ERO/ORR items
      posted_date: item.posted_date || item.event_start || null, // Keep original for formatting
      event_start: item.event_start || null // Keep original for formatting
    };
  }) : [];

  // Extract unique source_name values from provincial data (where source_channel = 'provincial')
  const sourceNames = useMemo(() => {
    if (!provincialData) return [];
    const uniqueSources = new Set<string>();
    provincialData.forEach((item: any) => {
      const sourceName = item.source_name || item.source;
      if (sourceName) {
        uniqueSources.add(sourceName);
      }
    });
    return Array.from(uniqueSources).sort();
  }, [provincialData]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Open": return "bg-green-100 text-green-700 border-green-200";
      case "Closed": return "bg-neutral-100 text-neutral-600 border-neutral-200";
      case "Under Review": return "bg-blue-100 text-blue-700 border-blue-200";
      default: return "bg-neutral-100 text-neutral-600 border-neutral-200";
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case "High": return "text-red-600 bg-red-50 border-red-100";
      case "Medium": return "text-orange-600 bg-orange-50 border-orange-100";
      case "Low": return "text-blue-600 bg-blue-50 border-blue-100";
      default: return "text-neutral-600 bg-neutral-50 border-neutral-100";
    }
  };

  const filteredItems = trackingItems.filter((item: any) => {
    if (activeTab === "active") return item.status !== "Closed";
    return true;
  }).filter((item: any) => 
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    item.description.toLowerCase().includes(searchQuery.toLowerCase())
  ).filter((item: any) => {
    // Filter by source_name if a specific source is selected
    if (selectedSourceName === "all") return true;
    return item.sourceName === selectedSourceName;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeTab, selectedSourceName]);

  // Scroll to top when page changes
  useEffect(() => {
    // Find the scrollable parent (main element from Layout)
    const scrollableParent = contentTopRef.current?.closest('main');
    if (scrollableParent) {
      scrollableParent.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  // Check if user has any municipalities monitoring
  const hasMunicipalities = monitoredMunicipalities && monitoredMunicipalities.length > 0;

  return (
    <div className="w-full">
      <main className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
        <div className="p-8 max-w-[1600px] mx-auto space-y-6">
          {/* Scroll target for pagination */}
          <div ref={contentTopRef} />
          
          {/* Page Header */}
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-foreground tracking-tight">Provincial Tracking</h1>
            <p className="text-muted-foreground">
              Tracking and monitoring {trackingItems.length} proposals from Ontario government sites
            </p>
          </div>
          
          {/* Control Toolbar */}
          <div className="bg-white dark:bg-neutral-900 p-2 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 w-full md:w-auto flex-1">
               <div className="relative w-full md:max-w-md">
                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                 <Input 
                   placeholder="Search by committee, topic, or keyword..." 
                   className="pl-9 bg-neutral-50 dark:bg-neutral-950 border-neutral-200 dark:border-neutral-800 h-9 text-sm"
                   value={searchQuery}
                   onChange={(e) => setSearchQuery(e.target.value)}
                 />
               </div>
               <DropdownMenu>
                 <DropdownMenuTrigger asChild>
                   <Button 
                     variant="outline" 
                     size="sm" 
                     className={cn(
                       "h-9 gap-2 bg-white dark:bg-neutral-900 text-neutral-600",
                       selectedSourceName !== "all" && "border-primary bg-primary/5"
                     )}
                   >
                     <Filter className="h-3.5 w-3.5" /> 
                     Filters
                     {selectedSourceName !== "all" && (
                       <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px] font-semibold">
                         1
                       </Badge>
                     )}
                   </Button>
                 </DropdownMenuTrigger>
                 <DropdownMenuContent align="end" className="w-56 max-h-[300px] overflow-y-auto">
                   <DropdownMenuLabel>Filter by Source</DropdownMenuLabel>
                   <DropdownMenuSeparator />
                   <DropdownMenuRadioGroup value={selectedSourceName} onValueChange={setSelectedSourceName}>
                     <DropdownMenuRadioItem value="all">
                       All Sources
                     </DropdownMenuRadioItem>
                     {sourceNames.length > 0 && <DropdownMenuSeparator />}
                     {sourceNames.map((sourceName) => (
                       <DropdownMenuRadioItem key={sourceName} value={sourceName}>
                         {sourceName}
                       </DropdownMenuRadioItem>
                     ))}
                   </DropdownMenuRadioGroup>
                 </DropdownMenuContent>
               </DropdownMenu>
            </div>

            <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
               {/* Status Toggle */}
               <div className="bg-neutral-100 dark:bg-neutral-800 p-1 rounded-lg flex items-center">
                 <button 
                   onClick={() => setActiveTab("active")}
                   className={cn(
                     "px-3 py-1 rounded-md text-xs font-medium transition-all",
                     activeTab === "active" ? "bg-white dark:bg-neutral-700 shadow-sm text-neutral-900 dark:text-white" : "text-neutral-500 hover:text-neutral-700"
                   )}
                 >
                   Active
                 </button>
                 <button 
                   onClick={() => setActiveTab("all")}
                   className={cn(
                     "px-3 py-1 rounded-md text-xs font-medium transition-all",
                     activeTab === "all" ? "bg-white dark:bg-neutral-700 shadow-sm text-neutral-900 dark:text-white" : "text-neutral-500 hover:text-neutral-700"
                   )}
                 >
                   All History
              </button>
               </div>

               <div className="w-px h-6 bg-neutral-200 dark:bg-neutral-800" />

               {/* View Toggle */}
               <div className="flex items-center gap-1">
                 <Button 
                   variant="ghost" 
                   size="icon" 
                   className={cn("h-8 w-8", viewMode === "list" && "bg-neutral-100 dark:bg-neutral-800 text-primary")}
                   onClick={() => setViewMode("list")}
                 >
                   <LayoutList className="h-4 w-4" />
                 </Button>
                 <Button 
                   variant="ghost" 
                   size="icon" 
                   className={cn("h-8 w-8", viewMode === "grid" && "bg-neutral-100 dark:bg-neutral-800 text-primary")}
                   onClick={() => setViewMode("grid")}
                 >
                   <LayoutGrid className="h-4 w-4" />
                 </Button>
               </div>
            </div>
          </div>

          {/* Content Area */}
          {!hasMunicipalities ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 border-2 border-dashed border-neutral-200 dark:border-neutral-800 rounded-xl bg-neutral-50/50 dark:bg-neutral-900/50"
            >
              <div className="h-16 w-16 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center mb-6">
                <BookOpen className="h-8 w-8 text-neutral-400" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">
                No Provincial Updates Found
              </h3>
              <p className="text-neutral-500 dark:text-neutral-400 max-w-md mb-8 leading-relaxed">
                You aren't monitoring any municipalities yet. Configure your monitoring criteria to start receiving updates on regulations and bills.
              </p>
              <div className="flex items-center gap-4">
                <Button variant="outline" className="gap-2 bg-white dark:bg-neutral-900" onClick={() => setIsLearnMoreOpen(true)}>
                  Learn More
                </Button>
                <Button className="gap-2 shadow-md bg-primary hover:bg-primary/90 text-white" onClick={() => navigate('/sources')}>
                  <PlusCircle className="h-4 w-4" />
                  Configure Monitoring
                </Button>
              </div>
            </motion.div>
          ) : (
            <>

          {/* Content Area */}
          <div className={cn("grid gap-4", viewMode === "grid" ? "grid-cols-1 xl:grid-cols-2" : "grid-cols-1")}>
            <AnimatePresence mode="popLayout">
              {paginatedItems.length > 0 ? paginatedItems.map((item: any, i: number) => (
                <motion.div
                  layout
                  key={item.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => setSelectedItem(item)}
                  className={cn(
                    "group bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200/60 dark:border-neutral-800 p-6 shadow-sm hover:shadow-md hover:border-primary/30 transition-all cursor-pointer relative overflow-hidden flex flex-col",
                    viewMode === "list" && "flex-row items-start gap-6"
                  )}
                >
                  {/* List View: Left Impact Indicator */}
                  {viewMode === "list" && (
                    <div className={cn("w-1.5 self-stretch rounded-full shrink-0", 
                      item.impact === "High" ? "bg-red-500" : item.impact === "Medium" ? "bg-orange-500" : "bg-blue-500"
                    )} />
                  )}

                  <div className={cn("flex flex-col w-full flex-1", viewMode === "list" && "flex-row items-center justify-between")}>
                    
                    <div className={cn("flex flex-col gap-3 flex-1", viewMode === "list" && "flex-1")}>
                      {/* Badges Row */}
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="bg-neutral-100 text-neutral-700 border-neutral-200 font-semibold">
                          {item.sourceShort}
                        </Badge>
                        {item.isEROORR && item.instrument_type ? (
                          <Badge variant="outline" className="font-medium bg-blue-50 text-blue-700 border-blue-200">
                            {item.instrument_type}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className={cn("font-medium", getStatusColor(item.status))}>
                            {item.status}
                          </Badge>
                        )}
                      </div>

                      {/* Title - Single line */}
                      <div>
                        <h3 className="text-lg font-bold text-primary dark:text-primary leading-snug group-hover:text-primary/80 transition-colors line-clamp-1">
                          {item.title}
                        </h3>
                      </div>

                      {/* Description - Fixed height for 4 lines to keep footer position consistent */}
                      {viewMode === "grid" && (
                        <div className="h-[5.5rem]">
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed line-clamp-4 mt-0.5">
                            {item.description}
                          </p>
                        </div>
                      )}
                    </div>
        
                    {/* Footer / Metadata */}
                    <div className={cn(
                      viewMode === "grid" 
                        ? "pt-4 border-t border-neutral-100 dark:border-neutral-800 mt-auto" 
                        : "flex items-center gap-8 shrink-0"
                    )}>
                      <div className={cn("flex text-xs text-neutral-500", viewMode === "grid" ? "items-center gap-4" : "flex-col gap-1 text-right min-w-[120px]")}>
                        {item.isEROORR && item.commentPeriod ? (
                          <div className="flex items-center gap-1.5">
                            {viewMode === "grid" && <Calendar className="h-3.5 w-3.5" />}
                            <span>Comment Period: <span className="font-medium text-neutral-700 dark:text-neutral-300">{item.commentPeriod}</span></span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            {viewMode === "grid" && <Calendar className="h-3.5 w-3.5" />}
                            <span>Published: <span className="font-medium text-neutral-700 dark:text-neutral-300">{formatDateShort(item.posted_date || item.event_start) || item.postedDate}</span></span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )) : (
                <div className="text-center py-12 text-neutral-500">
                  <p>No proposals found</p>
                </div>
              )}
            </AnimatePresence>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage > 1) setCurrentPage(currentPage - 1);
                      }}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  
                  {(() => {
                    const pages: (number | 'ellipsis')[] = [];
                    
                    if (totalPages <= 7) {
                      // Show all pages if 7 or fewer
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // Always show first page
                      pages.push(1);
                      
                      if (currentPage <= 4) {
                        // Near the start
                        for (let i = 2; i <= 5; i++) {
                          pages.push(i);
                        }
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      } else if (currentPage >= totalPages - 3) {
                        // Near the end
                        pages.push('ellipsis');
                        for (let i = totalPages - 4; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        // In the middle
                        pages.push('ellipsis');
                        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                          pages.push(i);
                        }
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      }
                    }
                    
                    return pages.map((page, index) => {
                      if (page === 'ellipsis') {
                        return (
                          <PaginationItem key={`ellipsis-${index}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return (
                        <PaginationItem key={page}>
                          <PaginationLink
                            href="#"
                            isActive={currentPage === page}
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(page);
                            }}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    });
                  })()}
                  
                  <PaginationItem>
                    <PaginationNext 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                      }}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
            </>
          )}

        </div>
        )}
      </main>

      {/* Details Dialog */}
      <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent className="max-w-3xl p-0 gap-0 border-none shadow-2xl overflow-hidden">
          {selectedItem && (
            <>
              {/* Dialog Header */}
              <div className="p-8 bg-white dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                     <Badge className="bg-[#1A1C1E] text-white hover:bg-[#1A1C1E]">
                       {selectedItem.source}
                     </Badge>
                     {selectedItem.isEROORR && selectedItem.instrument_type ? (
                       <Badge variant="outline" className="font-medium bg-blue-50 text-blue-700 border-blue-200">
                         {selectedItem.instrument_type}
                       </Badge>
                     ) : (
                       <Badge variant="outline" className={cn("font-medium", getStatusColor(selectedItem.status))}>
                         {selectedItem.status}
                       </Badge>
                     )}
      </div>

                  <h2 className="text-2xl font-bold text-neutral-900 dark:text-white leading-snug">
                    {selectedItem.title}
                  </h2>

                  {/* Category Name and Ministry Name for ERO/ORR items */}
                  {selectedItem.isEROORR && (selectedItem.category_name || selectedItem.ministry_name) && (
                    <div className="flex items-center gap-4 text-sm text-neutral-600 dark:text-neutral-400">
                      {selectedItem.category_name && (
                        <span className="font-medium">{selectedItem.category_name}</span>
                      )}
                      {selectedItem.category_name && selectedItem.ministry_name && (
                        <span className="text-neutral-300">•</span>
                      )}
                      {selectedItem.ministry_name && (
                        <span>{selectedItem.ministry_name}</span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-6 text-sm">
                    {selectedItem.isEROORR && selectedItem.commentPeriod ? (
                      <div className="flex items-center gap-2 text-neutral-500">
                        <div className="bg-blue-50 p-1.5 rounded-md">
                          <Calendar className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-600">Comment Period</div>
                          <div className="font-medium text-blue-700">{selectedItem.commentPeriod}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-neutral-500">
                        <div className="bg-neutral-100 p-1.5 rounded-md">
                          <Calendar className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-semibold">Published</div>
                          <div className="font-medium text-neutral-900 dark:text-neutral-200">{formatDateShort(selectedItem.posted_date || selectedItem.event_start) || selectedItem.postedDate}</div>
                        </div>
                      </div>
                    )}
                  </div>
          </div>
          </div>

              {/* Dialog Body */}
              <div className="p-8 bg-[#FAFAFA] dark:bg-neutral-950 max-h-[60vh] overflow-y-auto space-y-8">
                
                {/* Description Section */}
                <div className="space-y-3">
                  <h3 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 uppercase tracking-wide flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    Summary
                  </h3>
                  <div className="bg-white dark:bg-neutral-900 p-5 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
                    <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                      {selectedItem.description || "No description available."}
                    </p>
                    <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800 flex items-center gap-2 flex-wrap">
                      {selectedItem.isEROORR && selectedItem.bill_act ? (
                        <>
                          <span className="text-xs font-medium text-neutral-500">Bill Act:</span>
                          <Badge variant="secondary" className="bg-neutral-100 text-neutral-700">
                            {selectedItem.bill_act}
                          </Badge>
                        </>
                      ) : (
                        <>
                          <span className="text-xs font-medium text-neutral-500">Category:</span>
                          <Badge variant="secondary" className="bg-neutral-100 text-neutral-700">
                            {selectedItem.category}
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>
                </div>

              </div>

              {/* Dialog Footer */}
              <div className="p-6 bg-white dark:bg-neutral-900 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="gap-2 text-neutral-600">
                    <Download className="h-4 w-4" /> Attachments
                  </Button>
                  <Button variant="ghost" size="sm" className="gap-2 text-neutral-400 hover:text-neutral-600">
                    <Share2 className="h-4 w-4" />
                  </Button>
            </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" className="gap-2 border-primary/20 text-primary hover:bg-primary/5 hover:text-primary">
                    <Sparkles className="h-4 w-4" /> Ask AI
                  </Button>
                  <Button 
                    className="bg-primary hover:bg-primary/90 text-white shadow-sm gap-2"
                    onClick={() => {
                      if (selectedItem.url) {
                        window.open(selectedItem.url, '_blank');
                      }
                    }}
                    disabled={!selectedItem.url}
                  >
                    View Official Item <ArrowUpRight className="h-4 w-4" />
                  </Button>
        </div>
        </div>
          </>
        )}
        </DialogContent>
      </Dialog>

      {/* Learn More Dialog */}
      <Dialog open={isLearnMoreOpen} onOpenChange={setIsLearnMoreOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-primary" />
              About Provincial Monitoring
            </DialogTitle>
            <DialogDescription className="text-base">
              Cityscrape automatically monitors provincial regulatory bodies to keep you informed about legislative changes that impact your developments.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-4 rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800">
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <div className="h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs">1</div>
                  Sources We Track
                </h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  We monitor the Environmental Registry of Ontario (ERO), the Ontario Regulatory Registry (ORR), and 5 key ministries in real-time.
                </p>
              </div>
              
              <div className="p-4 rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800">
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <div className="h-6 w-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs">2</div>
                  AI Filtering
                </h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  Our AI analyzes thousands of proposals to identify only those that match your specific development criteria.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800">
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <div className="h-6 w-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs">3</div>
                  Impact Analysis
                </h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  Every proposal is automatically graded for potential impact (High/Medium/Low) on your business interests.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800">
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <div className="h-6 w-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">4</div>
                  Smart Alerts
                </h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  Get notified immediately when relevant deadlines, consultation periods, or decisions are posted.
                </p>
              </div>
            </div>

            <div className="bg-primary/5 border border-primary/10 rounded-lg p-4 flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-primary mb-1">Pro Tip</h4>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  You can customize your monitoring criteria in Settings to ensure you only see updates relevant to your specific sector (e.g., Residential, Commercial, Infrastructure).
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLearnMoreOpen(false)}>
              Close
            </Button>
            <Button className="bg-primary hover:bg-primary/90 text-white" onClick={() => setIsLearnMoreOpen(false)}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}